import { useParams, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { comics, categories } from "@/mocks/comics";
import ComicCard from "@/components/base/ComicCard";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<"latest" | "rating" | "views">("latest");
  const [statusFilter, setStatusFilter] = useState<"全部" | "连载中" | "已完结">("全部");

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredComics = useMemo(() => {
    let result = slug === "all" ? [...comics] : comics.filter((c) => c.category === slug);

    if (statusFilter !== "全部") {
      result = result.filter((c) => c.status === statusFilter);
    }

    switch (sortBy) {
      case "rating":
        result.sort((a, b) => b.rating - a.rating);
        break;
      case "views":
        result.sort((a, b) => b.views - a.views);
        break;
      case "latest":
      default:
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }

    return result;
  }, [slug, sortBy, statusFilter]);

  const sortOptions = [
    { value: "latest" as const, label: "最新更新" },
    { value: "rating" as const, label: "评分最高" },
    { value: "views" as const, label: "人气最高" },
  ];

  const statuses = ["全部", "连载中", "已完结"];

  return (
    <>
      <main className="min-h-screen bg-background-50 pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: "分类浏览", href: "/category/all" },
            { label: currentCategory.name },
          ]}
        />

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="mb-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {currentCategory.name}漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filteredComics.length} 部{currentCategory.name !== "全部" ? currentCategory.name : ""}韩漫作品
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.value
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              {statuses.map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    statusFilter === status
                      ? "bg-secondary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无符合条件的漫画</p>
            </div>
          )}

          {filteredComics.length > 10 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 text-sm cursor-pointer hover:bg-background-200 transition-colors duration-200">
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-sm font-medium cursor-pointer">1</button>
              <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm cursor-pointer hover:bg-background-200 transition-colors duration-200">2</button>
              <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm cursor-pointer hover:bg-background-200 transition-colors duration-200">3</button>
              <span className="text-foreground-400 text-sm">...</span>
              <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm cursor-pointer hover:bg-background-200 transition-colors duration-200">8</button>
              <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 text-sm cursor-pointer hover:bg-background-200 transition-colors duration-200">
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </main>
    </>
  );
}